/**
 * Subcontractors list screen.
 *
 * Lists SubcontractorRow rows for the authenticated org (GET /api/subcontractors).
 * Tapping a row navigates to the detail screen at /(app)/subcontractors/[id]. An
 * inline "+ New subcontractor" form creates a subcontractor (name, license number,
 * 1099 toggle).
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1). No raw style values.
 */
import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listSubcontractors,
  createSubcontractor,
  type CreateSubcontractorRequest,
  type SubcontractorRow,
} from '../../src/api/subcontractors';
import { Screen, Text, Badge, Card, Button, Field, Select, ListRow } from '../../src/components/ui';
import { theme } from '../../src/theme';
import { extractErrorMessage } from '../../src/lib/format';

const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
] as const;

export default function SubcontractorsScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [is1099, setIs1099] = useState<'yes' | 'no' | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['subcontractors'],
    queryFn: () => listSubcontractors(),
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateSubcontractorRequest) => createSubcontractor(body),
    onSuccess: (sub) => {
      void queryClient.invalidateQueries({ queryKey: ['subcontractors'] });
      queryClient.setQueryData(['subcontractor', sub.id], sub);
      setName('');
      setLicenseNumber('');
      setIs1099(null);
      setShowForm(false);
      router.push(`/(app)/subcontractors/${sub.id}`);
    },
    onError: (err) => setFormError(extractErrorMessage(err, 'Could not create subcontractor.')),
  });

  const onCreate = () => {
    setFormError(null);
    if (!name.trim()) {
      setFormError('Enter a name for the subcontractor.');
      return;
    }
    const body: CreateSubcontractorRequest = { name: name.trim() };
    if (licenseNumber.trim()) body.license_number = licenseNumber.trim();
    if (is1099) body.is_1099 = is1099 === 'yes';
    createMutation.mutate(body);
  };

  if (isLoading) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <ActivityIndicator
            size="large"
            color={theme.color.action.primary}
            accessibilityLabel="Loading subcontractors"
          />
          <Text variant="body" color="secondary">
            Loading subcontractors…
          </Text>
        </View>
      </Screen>
    );
  }

  if (isError) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(error, 'Failed to load subcontractors.')}
            </Text>
            <Button
              title="Retry"
              variant="secondary"
              size="sm"
              fullWidth={false}
              onPress={() => refetch()}
              accessibilityLabel="Retry loading subcontractors"
            />
          </Card>
        </View>
      </Screen>
    );
  }

  const rows = data ?? [];

  return (
    <Screen scroll>
      {showForm ? (
        <Card>
          <Text variant="bodyStrong">New subcontractor</Text>
          <Field
            label="Name"
            placeholder="e.g. Apex Electrical"
            value={name}
            onChangeText={setName}
            autoCapitalize="words"
            accessibilityLabel="Subcontractor name"
          />
          <Field
            label="License number (optional)"
            placeholder="e.g. EC13001234"
            value={licenseNumber}
            onChangeText={setLicenseNumber}
            autoCapitalize="characters"
            autoCorrect={false}
            accessibilityLabel="License number"
          />
          <Select
            label="1099 contractor?"
            options={YES_NO_OPTIONS}
            value={is1099}
            onChange={setIs1099}
          />
          {formError ? (
            <Text variant="small" color="danger" accessibilityRole="alert">
              {formError}
            </Text>
          ) : null}
          <Button
            title="Create subcontractor"
            onPress={onCreate}
            loading={createMutation.isPending}
            disabled={createMutation.isPending || !name.trim()}
            accessibilityLabel="Create subcontractor"
          />
          <Button
            title="Cancel"
            variant="ghost"
            onPress={() => {
              setShowForm(false);
              setFormError(null);
              setName('');
              setLicenseNumber('');
              setIs1099(null);
            }}
            accessibilityLabel="Cancel new subcontractor"
          />
        </Card>
      ) : (
        <Button
          title="+ New subcontractor"
          onPress={() => setShowForm(true)}
          accessibilityLabel="Create a new subcontractor"
        />
      )}

      <Text variant="label" color="secondary">
        {rows.length} {rows.length === 1 ? 'subcontractor' : 'subcontractors'}
      </Text>

      {rows.length === 0 ? (
        <Text variant="body" color="secondary">
          No subcontractors yet. Add your first one to get started.
        </Text>
      ) : (
        <View style={styles.list}>
          {rows.map((sub: SubcontractorRow) => (
            <ListRow
              key={sub.id}
              title={sub.name}
              subtitle={
                sub.license_number ? 'License ' + sub.license_number : 'No license on file'
              }
              right={
                <View style={styles.rightSlot}>
                  {sub.is_1099 ? <Badge status="sent" label="1099" /> : null}
                  {!sub.is_active ? <Badge status="draft" label="Inactive" /> : null}
                </View>
              }
              onPress={() => router.push(`/(app)/subcontractors/${sub.id}`)}
            />
          ))}
        </View>
      )}
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing['2xl'],
  },
  list: {
    gap: theme.spacing.sm,
  },
  rightSlot: {
    alignItems: 'flex-end',
    gap: theme.spacing.xs,
  },
});
